import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { RefreshCw, Webhook, Clock, FileText, CheckCircle, Upload } from 'lucide-react';
import { useMemedIntegration } from '@/hooks/useMemedIntegration';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/badge';
import { MemedIntegrationStatus } from '@/components/memed/MemedIntegrationStatus';
import { MemedSyncButton } from '@/components/memed/MemedSyncButton';
import { MemedSettingsCard } from '@/components/memed/MemedSettingsCard';
import { memedApi, memedConfigApi, httpClient } from '@/api';
import { useToast } from '@/providers/ToastProvider';

const recentPrescriptions = [
  { id: '1', patient: 'Maria Silva', date: '2026-09-08', status: 'SIGNED' },
  { id: '2', patient: 'João Santos', date: '2026-09-07', status: 'SYNCED' },
  { id: '3', patient: 'Ana Oliveira', date: '2026-09-06', status: 'SIGNED' },
  { id: '4', patient: 'Carlos Ferreira', date: '2026-09-05', status: 'FAILED' },
  { id: '5', patient: 'Paula Costa', date: '2026-09-04', status: 'SYNCED' },
];

export function DoctorMemedSettingsPage() {
  const { status, isLoading, sync, isSyncing } = useMemedIntegration();
  const { toast } = useToast();
  const [receituarioTheme, setReceituarioTheme] = useState('2');
  const [validityPeriod, setValidityPeriod] = useState('30');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [webhookTestResult, setWebhookTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const { data: memedConfig } = useQuery({
    queryKey: ['memed', 'config'],
    queryFn: () => memedConfigApi.getConfig(),
  });

  useEffect(() => {
    if (memedConfig?.receituarioTheme) {
      setReceituarioTheme(String(memedConfig.receituarioTheme));
    }
  }, [memedConfig]);

  const saveConfigMutation = useMutation({
    mutationFn: async () => {
      await httpClient.patch('/memed/config', {
        receituarioTheme: parseInt(receituarioTheme),
        validityDays: parseInt(validityPeriod),
      });
    },
    onSuccess: () => {
      setSaveSuccess(true);
      toast({ title: 'Configurações MeMed salvas', variant: 'success' });
      setTimeout(() => setSaveSuccess(false), 3000);
    },
    onError: (error) => {
      const msg = error instanceof Error ? error.message : 'Erro ao salvar';
      toast({ title: 'Erro ao salvar', description: msg, variant: 'error' });
    },
  });

  const testWebhookMutation = useMutation({
    mutationFn: async () => {
      const result = await memedApi.getIntegrationStatus();
      return { success: result.isLinked, message: result.isLinked ? 'Webhook conectado com sucesso' : 'Falha na conexão' };
    },
    onSuccess: (data) => {
      setWebhookTestResult(data);
      setTimeout(() => setWebhookTestResult(null), 5000);
    },
    onError: () => {
      setWebhookTestResult({ success: false, message: 'Erro ao testar webhook' });
      setTimeout(() => setWebhookTestResult(null), 5000);
    },
  });

  const handleSaveConfig = () => {
    saveConfigMutation.mutate();
  };

  const handleTestWebhook = () => {
    testWebhookMutation.mutate();
  };

  const handleViewLogs = () => {
    toast({
      title: 'Logs de Integração',
      description: 'O histórico detalhado de logs da MeMed estará disponível em breve.',
      variant: 'info',
    });
  };

  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [uploadSuccessMsg, setUploadSuccessMsg] = useState<string | null>(null);

  const handleTemplateUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingPdf(true);
    setUploadSuccessMsg(null);

    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64 = reader.result as string;
          await httpClient.post('/memed/config/upload-template', {
            base64Pdf: base64,
            filename: file.name,
          });
          setUploadSuccessMsg('Papel timbrado pessoal carregado e ativado no seu receituário com sucesso!');
          toast({
            title: 'Papel timbrado atualizado!',
            description: 'Seu receituário digital agora utilizará seu próprio layout timbrado.',
            variant: 'success',
          });
        } catch (err: any) {
          toast({
            title: 'Erro no envio do papel timbrado',
            description: err?.message || 'Tente novamente.',
            variant: 'error',
          });
        } finally {
          setUploadingPdf(false);
        }
      };
      reader.readAsDataURL(file);
    } catch {
      setUploadingPdf(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Configurações da Memed</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Configurações / Integrações / Memed
          </p>
        </div>
      </div>

      <MemedIntegrationStatus />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <MemedSettingsCard
            icon={FileText}
            title="Configurações de Receituário"
          >
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-3">
                  Tema do Receituário
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {[
                    { id: 1, name: 'Clássico Azul', desc: 'Cabeçalho azul com logotipo centralizado e linhas nítidas', accent: 'bg-blue-600', layout: 'border-t-4 border-t-blue-600' },
                    { id: 2, name: 'Moderno Esmeralda', desc: 'Estilo limpo com destaque verde-saúde e grid minimalista', accent: 'bg-emerald-600', layout: 'border-l-4 border-l-emerald-600' },
                    { id: 3, name: 'Elegante Navy', desc: 'Bordas duplas sóbrias com tipografia serifada profissional', accent: 'bg-indigo-900', layout: 'border-2 border-indigo-900' },
                    { id: 4, name: 'Minimalista Neutro', desc: 'Design monocromático ultra limpo focado em legibilidade', accent: 'bg-slate-700', layout: 'border border-dashed border-slate-400' },
                  ].map((theme) => (
                    <label
                      key={theme.id}
                      className={`
                        relative flex flex-col p-3.5 border-2 rounded-2xl cursor-pointer transition-all duration-200
                        ${receituarioTheme === String(theme.id)
                          ? 'border-primary bg-primary/10 shadow-md ring-2 ring-primary/20 scale-[1.02]'
                          : 'border-border hover:border-primary/40 bg-card hover:shadow-xs'
                        }
                      `}
                    >
                      <input
                        type="radio"
                        name="receituarioTheme"
                        value={theme.id}
                        checked={receituarioTheme === String(theme.id)}
                        onChange={(e) => setReceituarioTheme(e.target.value)}
                        className="sr-only"
                      />
                      {/* Miniatura visual realista do receituário */}
                      <div className={`
                        w-full h-24 rounded-xl border mb-3 p-2 flex flex-col justify-between shadow-xs bg-white dark:bg-slate-900
                        ${theme.layout}
                      `}>
                        <div className="flex items-center justify-between border-b pb-1">
                          <span className={`w-3 h-3 rounded-full ${theme.accent}`} />
                          <span className="text-[9px] font-mono text-muted-foreground uppercase font-bold">RECEITA</span>
                        </div>
                        <div className="space-y-1 my-auto">
                          <div className="h-1.5 w-3/4 bg-slate-200 dark:bg-slate-700 rounded-full" />
                          <div className="h-1 w-1/2 bg-slate-100 dark:bg-slate-800 rounded-full" />
                        </div>
                        <div className="flex justify-end pt-1 border-t border-slate-100 dark:border-slate-800">
                          <span className="text-[8px] text-muted-foreground font-mono">Assinatura ICP</span>
                        </div>
                      </div>
                      <span className="text-xs font-bold text-foreground block">{theme.name}</span>
                      <span className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{theme.desc}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Prazo de Validade Padrão
                </label>
                <select
                  value={validityPeriod}
                  onChange={(e) => setValidityPeriod(e.target.value)}
                  className="w-full max-w-xs px-3.5 py-2.5 border border-border bg-background text-foreground rounded-xl focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                >
                  <option value="15">15 dias</option>
                  <option value="30">30 dias</option>
                  <option value="60">60 dias</option>
                  <option value="90">90 dias</option>
                </select>
              </div>

              <div className="flex justify-end">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSaveConfig}
                  disabled={saveConfigMutation.isPending}
                  className="rounded-xl"
                >
                  {saveConfigMutation.isPending ? 'Salvando...' : saveSuccess ? 'Salvo com Sucesso!' : 'Salvar Configurações'}
                </Button>
              </div>
            </div>
          </MemedSettingsCard>

          <MemedSettingsCard
            icon={Webhook}
            title="Webhook de Sincronização"
          >
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-muted/40 rounded-xl border border-border">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-emerald-500/10 rounded-xl">
                    <CheckCircle className="h-5 w-5 text-emerald-500" />
                  </div>
                  <div>
                    <p className="font-bold text-foreground text-sm">Webhook Ativo</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      https://api.medikei.com.br/webhooks/memed
                    </p>
                  </div>
                </div>
                <Badge variant="success">Conectado</Badge>
              </div>

              {webhookTestResult && (
                <div className={`p-3 rounded-xl text-sm border ${webhookTestResult.success ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20' : 'bg-destructive/10 text-destructive border-destructive/20'}`}>
                  {webhookTestResult.message}
                </div>
              )}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleTestWebhook}
                  disabled={testWebhookMutation.isPending}
                  className="rounded-xl border-border"
                >
                  {testWebhookMutation.isPending ? 'Testando...' : 'Testar Conexão'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleViewLogs}
                  className="rounded-xl border-border"
                >
                  Ver Logs
                </Button>
              </div>
            </div>
          </MemedSettingsCard>

          <MemedSettingsCard
            icon={Upload}
            title="Papel Timbrado Pessoal (Cabeçalho e Rodapé)"
          >
            <div className="space-y-4">
              <p className="text-xs text-muted-foreground leading-relaxed">
                Importe seu próprio papel timbrado em formato PDF. A MeMed integrará seu cabeçalho e rodapé oficial ao fundo de todas as suas receitas digitais.
              </p>

              <div className="flex flex-col sm:flex-row items-center gap-3">
                <label className="flex-1 w-full flex items-center justify-center gap-2 p-3.5 border border-dashed border-border hover:border-primary/50 bg-muted/20 hover:bg-muted/40 rounded-xl cursor-pointer transition-colors text-xs text-muted-foreground hover:text-foreground">
                  <Upload className="h-4 w-4 text-primary" />
                  <span>{uploadingPdf ? 'Enviando PDF...' : 'Selecionar Arquivo PDF (Timbrado Pessoal)'}</span>
                  <input type="file" accept="application/pdf" onChange={handleTemplateUpload} disabled={uploadingPdf} className="hidden" />
                </label>
              </div>

              {uploadSuccessMsg && (
                <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs rounded-xl font-semibold">
                  {uploadSuccessMsg}
                </div>
              )}
            </div>
          </MemedSettingsCard>

          <MemedSettingsCard
            icon={Clock}
            title="Histórico de Receitas"
          >
            <div className="space-y-3">
              {recentPrescriptions.map((prescription) => (
                <div
                  key={prescription.id}
                  className="flex items-center justify-between p-3.5 bg-muted/30 rounded-xl border border-border"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-card rounded-xl border border-border">
                      <FileText className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-foreground">{prescription.patient}</p>
                      <p className="text-xs text-muted-foreground">{prescription.date}</p>
                    </div>
                  </div>
                  <Badge
                    variant={
                      prescription.status === 'SIGNED' ? 'success' :
                      prescription.status === 'SYNCED' ? 'primary' :
                      prescription.status === 'FAILED' ? 'error' :
                      'warning'
                    }
                  >
                    {prescription.status === 'SIGNED' ? 'Assinado' :
                     prescription.status === 'SYNCED' ? 'Sincronizado' :
                     prescription.status === 'FAILED' ? 'Erro' :
                     'Pendente'}
                  </Badge>
                </div>
              ))}
            </div>
          </MemedSettingsCard>
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl bg-card border border-border shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 bg-primary/10 rounded-xl">
                <RefreshCw className="h-5 w-5 text-primary" />
              </div>
              <h3 className="font-bold text-lg text-foreground">Sincronização</h3>
            </div>
            <MemedSyncButton
              onSync={() => sync()}
              isLoading={isLoading || isSyncing}
              lastSyncTime={status?.lastSyncAt}
              lastError={status?.lastError}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
